import { useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import {
  PlusIcon,
  ChevronUpDownIcon,
  CheckIcon,
  ShieldExclamationIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { examApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/ui';
import type { BadgeVariant } from '../components/ui';
import clsx from 'clsx';
import { useTeamId } from '../hooks/useTeamId';

const EXAM_TYPES = [
  { value: 'FDIC_SAFETY_SOUNDNESS', label: 'FDIC Safety & Soundness' },
  { value: 'FDIC_COMPLIANCE', label: 'FDIC Compliance' },
  { value: 'OCC', label: 'OCC' },
  { value: 'STATE', label: 'State' },
  { value: 'CFPB', label: 'CFPB' },
  { value: 'CRA', label: 'CRA' },
  { value: 'BSA_AML', label: 'BSA/AML' },
  { value: 'NCUA', label: 'NCUA' },
  { value: 'JOINT', label: 'Joint' },
  { value: 'OTHER', label: 'Other' },
];

const STATUS_CONFIG: Record<string, { label: string; className: string; variant: BadgeVariant }> = {
  PLANNING: { label: 'Planning', className: 'bg-gray-100 text-gray-700', variant: 'neutral' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-blue-100 text-blue-700', variant: 'info' },
  FIELDWORK_COMPLETE: { label: 'Fieldwork Complete', className: 'bg-yellow-100 text-yellow-700', variant: 'warning' },
  REPORT_PENDING: { label: 'Report Pending', className: 'bg-orange-100 text-orange-700', variant: 'warning' },
  CLOSED: { label: 'Closed', className: 'bg-green-100 text-green-700', variant: 'success' },
};

const STATUS_FILTERS = [
  { value: '', label: 'All Statuses' },
  { value: 'PLANNING', label: 'Planning' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'FIELDWORK_COMPLETE', label: 'Fieldwork Complete' },
  { value: 'REPORT_PENDING', label: 'Report Pending' },
  { value: 'CLOSED', label: 'Closed' },
];

export default function ExamManagementPage() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  // Get user's team
  const { teamId } = useTeamId();

  // Fetch exams
  const { data: examsData, isLoading } = useQuery({
    queryKey: ['exams', teamId, statusFilter],
    queryFn: () => examApi.list(teamId!, statusFilter ? { status: statusFilter } : undefined),
    enabled: !!teamId,
  });

  const exams = examsData?.data?.exams || [];

  // Create exam mutation
  const [formData, setFormData] = useState({
    examType: 'FDIC_COMPLIANCE',
    examinerAgency: '',
    scheduledStartDate: '',
    scheduledEndDate: '',
    leadExaminer: '',
    leadExaminerContact: '',
    focusAreas: '',
    notes: '',
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => examApi.create(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exams'] });
      toast.success('Exam created successfully');
      setShowCreate(false);
      setFormData({
        examType: 'FDIC_COMPLIANCE',
        examinerAgency: '',
        scheduledStartDate: '',
        scheduledEndDate: '',
        leadExaminer: '',
        leadExaminerContact: '',
        focusAreas: '',
        notes: '',
      });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to create exam');
    },
  });

  const handleCreate = () => {
    createMutation.mutate({
      examType: formData.examType,
      examinerAgency: formData.examinerAgency,
      scheduledStartDate: formData.scheduledStartDate || undefined,
      scheduledEndDate: formData.scheduledEndDate || undefined,
      leadExaminer: formData.leadExaminer || undefined,
      leadExaminerContact: formData.leadExaminerContact || undefined,
      focusAreas: formData.focusAreas ? formData.focusAreas.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
      notes: formData.notes || undefined,
    });
  };

  // Summary counts
  const activeExams = exams.filter((e: any) => e.status === 'IN_PROGRESS' || e.status === 'FIELDWORK_COMPLETE').length;
  const planningExams = exams.filter((e: any) => e.status === 'PLANNING').length;
  const totalFindings = exams.reduce((sum: number, e: any) => sum + (e._count?.findings || 0), 0);
  const upcomingExams = exams.filter((e: any) => {
    if (!e.scheduledStartDate || e.status === 'CLOSED') return false;
    return new Date(e.scheduledStartDate) > new Date();
  }).length;

  return (
    <FeatureGate feature="exam_management" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Exam Management"
          description="Track regulatory exams, documents, findings, and examiner requests"
          actions={
            <PermissionGate teamId={teamId} permission="compliance.write">
              <div className="flex items-center gap-2">
                <Link
                  to="/exams/bulk-import"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  title="Bulk-import exam history from another vendor"
                >
                  Bulk import
                </Link>
                <button
                  onClick={() => setShowCreate(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
                >
                  <PlusIcon className="h-4 w-4" />
                  New Exam
                </button>
              </div>
            </PermissionGate>
          }
        />

        {/* Summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-50 p-2">
                <ClockIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Upcoming</p>
                <p className="text-2xl font-bold text-gray-900">{upcomingExams}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-50 p-2">
                <DocumentMagnifyingGlassIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Exams</p>
                <p className="text-2xl font-bold text-gray-900">{activeExams}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gray-50 p-2">
                <ShieldExclamationIcon className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Planning</p>
                <p className="text-2xl font-bold text-gray-900">{planningExams}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-orange-50 p-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Findings</p>
                <p className="text-2xl font-bold text-gray-900">{totalFindings}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <Listbox value={statusFilter} onChange={setStatusFilter}>
            <div className="relative w-52">
              <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <span>{STATUS_FILTERS.find((f) => f.value === statusFilter)?.label}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {STATUS_FILTERS.map((f) => (
                    <Listbox.Option
                      key={f.value}
                      value={f.value}
                      className={({ active }) =>
                        clsx('relative cursor-pointer select-none py-2 pl-10 pr-4 text-sm', active ? 'bg-blue-50 text-blue-700' : 'text-gray-900')
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={clsx('block truncate', selected && 'font-medium')}>{f.label}</span>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <CheckIcon className="h-4 w-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </div>

        {/* Exams table */}
        <div className="rounded-lg border bg-white">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : exams.length === 0 ? (
            <EmptyState
              icon={DocumentMagnifyingGlassIcon}
              title="No exams found"
              description="Create your first exam to get started."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Agency</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Scheduled</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Docs</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Findings</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Requests</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {exams.map((exam: any) => {
                    const statusCfg = STATUS_CONFIG[exam.status] || STATUS_CONFIG.PLANNING;
                    const typeLabel = EXAM_TYPES.find((t) => t.value === exam.examType)?.label || exam.examType;
                    return (
                      <tr key={exam.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-6 py-4">
                          <Link to={`/exams/${exam.id}`} className="text-sm font-medium text-blue-600 hover:underline">
                            {typeLabel}
                          </Link>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">{exam.examinerAgency}</td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <StatusBadge variant={statusCfg.variant}>
                            {statusCfg.label}
                          </StatusBadge>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {exam.scheduledStartDate
                            ? new Date(exam.scheduledStartDate).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{exam._count?.documents || 0}</td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{exam._count?.findings || 0}</td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{exam._count?.requests || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Exam Modal */}
        <Transition appear show={showCreate} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowCreate(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
              leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">New Exam</Dialog.Title>
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Exam Type</label>
                          <select
                            value={formData.examType}
                            onChange={(e) => setFormData({ ...formData, examType: e.target.value })}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            {EXAM_TYPES.map((t) => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Agency</label>
                          <input
                            type="text"
                            value={formData.examinerAgency}
                            onChange={(e) => setFormData({ ...formData, examinerAgency: e.target.value })}
                            placeholder="e.g., FDIC, OCC"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Scheduled Start</label>
                          <input
                            type="date"
                            value={formData.scheduledStartDate}
                            onChange={(e) => setFormData({ ...formData, scheduledStartDate: e.target.value })}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Scheduled End</label>
                          <input
                            type="date"
                            value={formData.scheduledEndDate}
                            onChange={(e) => setFormData({ ...formData, scheduledEndDate: e.target.value })}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Lead Examiner</label>
                          <input
                            type="text"
                            value={formData.leadExaminer}
                            onChange={(e) => setFormData({ ...formData, leadExaminer: e.target.value })}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Contact</label>
                          <input
                            type="text"
                            value={formData.leadExaminerContact}
                            onChange={(e) => setFormData({ ...formData, leadExaminerContact: e.target.value })}
                            placeholder="Email or phone"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Focus Areas</label>
                        <input
                          type="text"
                          value={formData.focusAreas}
                          onChange={(e) => setFormData({ ...formData, focusAreas: e.target.value })}
                          placeholder="Marketing, HMDA, BSA (comma-separated)"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Notes</label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          rows={3}
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setShowCreate(false)}
                        className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreate}
                        disabled={!formData.examinerAgency || createMutation.isPending}
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                      >
                        {createMutation.isPending ? 'Creating...' : 'Create Exam'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}
